
import React, { useState } from 'react';
import ApiKeyDisplay from './ApiKeyDisplay';
import SyntaxHighlighter from '../common/SyntaxHighlighter';
import Terminal from '../common/Terminal';

const NextIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);

interface ProjectWalkthroughProps {
  projectName: string;
  onComplete: () => void;
  apiKey: string;
}

export default function ProjectWalkthrough({ projectName, onComplete, apiKey }: ProjectWalkthroughProps) {
  const [step, setStep] = useState(1);
  const totalSteps = 3;
  
  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      onComplete();
    }
  };
  
  const handlePrevious = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const terminalCommands = [
    { 
      input: 'npm install @abra-actions/sdk', 
      output: 'Added package @abra-actions/sdk@1.2.0' 
    },
    { 
      input: 'npx abra-actions init', 
      output: 'Created actionRegistry.ts in src/abra-actions/\nGenerated abra.config.ts in project root\nSetup complete! 🎉' 
    }
  ];

  const actionRegistryCode = `// AUTO-GENERATED BY ABRA CLI — DO NOT EDIT MANUALLY
import {

  getPolicyForUser,
  updateAdminPermissions, 
} from '../admin/handlers'

const actionRegistry: RegistryEntry[] = [

  { 
    name: "Get Policy By User",
    function: getPolicyForUser, 
    description: "Fetches a specific policy for a given user",
    suggested: true,
    suggestion: "Get the Content Policy for Acme Inc."
  },
  {
    name: "Update Administrator Permissions",
    function: updateAdminPermissions,
    description: "Update the Permissions for a Site Administrator",
    suggested: true,
    suggestion: "Give the Admin for Acme Inc. policy modifiction permission"
  },
]

export type RegistryEntry = {
  name: string,
  function: Function,
  description?: string,
  suggested?: boolean,
  suggestion?: string,
}

export default actionRegistry;`;

  const configCodeStart = `// AUTO-GENERATED BY ABRA CLI


import actionRegistry from './abra-actions/actionRegistry';
import actions from './abra-actions/__generated__/actions.json';

const abraConfig = {
  apiKey: "`;

  const configCodeEnd = `",
  actionRegistry,
  actions: actions.actions
};

export default abraConfig;`;
  
  return (
    <div className="new-project-form">
      <h2 className="form-title">Get Started with {projectName}</h2>
      <p className="form-description">
        Follow this guide to implement Abra in your application
      </p>
      
      <div className="setup-steps-progress">
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div 
            key={i} 
            className={`setup-step-indicator ${i + 1 === step ? 'active' : ''} ${i + 1 < step ? 'completed' : ''}`}
            onClick={() => i + 1 < step && setStep(i + 1)}
          />
        ))}
      </div>
      
      {step === 1 && (
        <div className="setup-step">
          <h3 className="setup-step-title">Installing Abra SDK</h3>
          <Terminal commands={terminalCommands} />
        </div>
      )}
      
      {step === 2 && (
        <div className="setup-step">
          <h3 className="setup-step-title">Review Your Action Registry</h3>
          <div className="code-editor">
            <div className="code-editor-header">
              <span className="code-editor-filename" data-ext="ts">src/abra-actions/actionRegistry.ts</span>
            </div>
            <div className="code-editor-content">
              <SyntaxHighlighter code={actionRegistryCode} language="typescript" />
            </div>
          </div>
        </div>
      )}
      
      {step === 3 && (
        <div className="setup-step">
          <h3 className="setup-step-title">Configure Your API Key</h3>
          <div className="code-editor">
            <div className="code-editor-header">
              <span className="code-editor-filename" data-ext="ts">abra.config.ts</span>
            </div>
            <div className="code-editor-content syntax-highlighted">
              <pre className="language-typescript">
                <code>
                  {configCodeStart}<ApiKeyDisplay apiKey={apiKey} />{configCodeEnd}
                </code>
              </pre>
            </div>
          </div>
          <p className="setup-instruction">
            You can find/regenerate your API key in the settings tab after completing this walkthrough.
          </p>
        </div>
      )}
      
      <div className="setup-navigation">
        {step > 1 && (
          <button className="button-secondary" onClick={handlePrevious}>
            Back
          </button>
        )}
        <button className="button-primary" onClick={handleNext}>
          {step === totalSteps ? (
            <>Complete Guide</>
          ) : (
            <>Next<NextIcon /></>
          )}
        </button>
      </div>
    </div>
  );
}